import { Link, useLocation } from 'react-router-dom';
import { Home, PhoneCall, Calendar, Settings, Mic, LogOut } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/auth-context';

type SidebarItem = {
  title: string;
  icon: React.ReactNode;
  href: string;
  disabled?: boolean;
};

export function Sidebar() {
  const { pathname } = useLocation();
  const { logout } = useAuth();

  const sidebarItems: SidebarItem[] = [
    {
      title: 'Dashboard',
      icon: <Home className="h-5 w-5" />,
      href: '/dashboard',
    },
    {
      title: 'AI Agents',
      icon: <PhoneCall className="h-5 w-5" />,
      href: '/ai-agents',
    },
    {
      title: 'Calendar Integrations',
      icon: <Calendar className="h-5 w-5" />,
      href: '/integrations/calendar',
    },
    {
      title: 'Voice Configurations',
      icon: <Mic className="h-5 w-5" />,
      href: '/voice-configurations',
    },
    {
      title: 'Settings',
      icon: <Settings className="h-5 w-5" />,
      href: '/settings',
    },
  ];

  return (
    <aside className="hidden md:flex h-screen w-64 flex-col border-r bg-card px-4 py-8">
      {/* Logo and app name */}
      <div className="flex items-center gap-2 px-2 mb-8">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary">
          <PhoneCall className="h-6 w-6 text-primary-foreground" />
        </div>
        <div className="font-semibold text-xl">AI Secretary</div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1">
        {sidebarItems.map((item) => (
          <Link
            key={item.href}
            to={item.disabled ? '#' : item.href}
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              pathname === item.href
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted text-muted-foreground hover:text-foreground',
              item.disabled && 'pointer-events-none opacity-60'
            )}
          >
            {item.icon}
            {item.title}
          </Link>
        ))}
      </nav>

      {/* Logout button */}
      <Button
        variant="ghost"
        className="flex w-full items-center justify-start gap-3 px-3 py-2"
        onClick={logout}
      >
        <LogOut className="h-5 w-5" />
        Logout
      </Button>
    </aside>
  );
}
